import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'

const PHASES = [
  { key: 'knockout_r32', label: 'Round of 32' },
  { key: 'knockout_r16', label: 'Round of 16' },
  { key: 'knockout_qf', label: 'Quarter-finals' },
  { key: 'knockout_sf', label: 'Semi-finals' },
  { key: 'knockout_3rd', label: '3rd Place Play-off' },
  { key: 'knockout_final', label: 'Final' }
]

function getWinner(pred, match) {
  if (!pred || pred.home === '' || pred.away === '' || pred.home === undefined || pred.away === undefined) return null
  const h = parseInt(pred.home)
  const a = parseInt(pred.away)
  if (isNaN(h) || isNaN(a)) return null
  if (h > a) return match.home
  if (a > h) return match.away
  if (h === a && pred.penalties_winner) {
    if (pred.penalties_winner === 'home') return match.home
    if (pred.penalties_winner === 'away') return match.away
  }
  return null
}

function getLoser(pred, match) {
  const winner = getWinner(pred, match)
  if (!winner) return null
  if (winner.id === match.home?.id) return match.away
  return match.home
}

function buildBracket(matches, predictions) {
  const resolved = {}
  const r32 = matches.filter(m => m.phase === 'knockout_r32')
  r32.forEach(m => { resolved[m.match_number] = { ...m, home: m.home || null, away: m.away || null } })

  const r16pairs = [[74,77],[73,75],[76,78],[79,80],[83,84],[81,82],[86,88],[85,87]]
  matches.filter(m => m.phase === 'knockout_r16').forEach((m, i) => {
    const m1 = resolved[r16pairs[i][0]]
    const m2 = resolved[r16pairs[i][1]]
    resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
  })

  const qfpairs = [[89,90],[93,94],[91,92],[95,96]]
  matches.filter(m => m.phase === 'knockout_qf').forEach((m, i) => {
    const m1 = resolved[qfpairs[i][0]]
    const m2 = resolved[qfpairs[i][1]]
    resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
  })

  const sfpairs = [[97,98],[99,100]]
  matches.filter(m => m.phase === 'knockout_sf').forEach((m, i) => {
    const m1 = resolved[sfpairs[i][0]]
    const m2 = resolved[sfpairs[i][1]]
    resolved[m.match_number] = { ...m, home: m1 ? getWinner(predictions[m1.id], m1) : null, away: m2 ? getWinner(predictions[m2.id], m2) : null }
  })

  const finalMatch = matches.find(m => m.phase === 'knockout_final')
  if (finalMatch) {
    const sf1 = resolved[101]
    const sf2 = resolved[102]
    resolved[finalMatch.match_number] = { ...finalMatch, home: sf1 ? getWinner(predictions[sf1.id], sf1) : null, away: sf2 ? getWinner(predictions[sf2.id], sf2) : null }
  }

  const thirdMatch = matches.find(m => m.phase === 'knockout_3rd')
  if (thirdMatch) {
    const sf1 = resolved[101]
    const sf2 = resolved[102]
    resolved[thirdMatch.match_number] = { ...thirdMatch, home: sf1 ? getLoser(predictions[sf1.id], sf1) : null, away: sf2 ? getLoser(predictions[sf2.id], sf2) : null }
  }

  return resolved
}

export default function Phase2Entry() {
  const { entryId } = useParams()
  const [entry, setEntry] = useState(null)
  const [matches, setMatches] = useState([])
  const [predictions, setPredictions] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [locked, setLocked] = useState(false)
  const [error, setError] = useState(null)
  const [activePhase, setActivePhase] = useState('knockout_r32')

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { window.location.href = '/login'; return }

      const { data: entryData, error: entryError } = await supabase
        .from('entries').select('*').eq('id', entryId).eq('user_id', user.id).single()
      if (entryError || !entryData) { setError('Entry not found.'); setLoading(false); return }
      setEntry(entryData)

      const { data: settingsData } = await supabase
        .from('settings').select('value').eq('key', 'phase2_lock_date').single()
      if (settingsData && new Date() > new Date(settingsData.value)) setLocked(true)

      const { data: matchesData } = await supabase
        .from('matches')
        .select('*, home:home_team_id(id,name,flag_emoji), away:away_team_id(id,name,flag_emoji)')
        .in('phase', ['knockout_r32','knockout_r16','knockout_qf','knockout_sf','knockout_3rd','knockout_final'])
        .order('match_number')
      setMatches(matchesData || [])

      const { data: existingPreds } = await supabase
        .from('match_predictions')
        .select('*')
        .eq('entry_id', entryId)
        .in('match_id', matchesData?.map(m => m.id) || [])

      // Pass 1 - resolve R32 penalty winners using DB team IDs (populated for R32)
      const predMap = {}
      existingPreds?.forEach(p => {
        const match = matchesData?.find(m => m.id === p.match_id)
        let penalties_winner = null
        if (p.predicted_penalties_winner) {
          penalties_winner = p.predicted_penalties_winner
        } else if (p.predicted_penalties_winner_id && match?.phase === 'knockout_r32') {
          if (p.predicted_penalties_winner_id === match?.home_team_id) penalties_winner = 'home'
          else if (p.predicted_penalties_winner_id === match?.away_team_id) penalties_winner = 'away'
        }
        predMap[p.match_id] = {
          home: p.predicted_home_goals,
          away: p.predicted_away_goals,
          penalties_winner
        }
      })

      // Pass 2 - build bracket with R32 penalty winners correctly resolved
      const initialBracket = buildBracket(matchesData || [], predMap)

      // Pass 3 - resolve R16+ penalty winners using the cascaded bracket
      existingPreds?.forEach(p => {
        if (!p.predicted_penalties_winner && !p.predicted_penalties_winner_id) return
        const match = matchesData?.find(m => m.id === p.match_id)
        if (!match || match.phase === 'knockout_r32') return
        if (p.predicted_penalties_winner) return
        const resolved = initialBracket[match.match_number]
        if (!resolved?.home?.id || !resolved?.away?.id) return
        if (p.predicted_penalties_winner_id === resolved.home.id) {
          predMap[p.match_id].penalties_winner = 'home'
        } else if (p.predicted_penalties_winner_id === resolved.away.id) {
          predMap[p.match_id].penalties_winner = 'away'
        }
      })

      setPredictions(predMap)
      setLoading(false)
    }
    load()
  }, [entryId])

  function setScore(matchId, side, value) {
    if (locked) return
    setPredictions(prev => ({ ...prev, [matchId]: { ...prev[matchId], [side]: value === '' ? '' : parseInt(value) || 0 } }))
  }

  function setPenaltyWinner(matchId, value) {
    if (locked) return
    setPredictions(prev => ({ ...prev, [matchId]: { ...prev[matchId], penalties_winner: value } }))
  }

  function isDraw(pred) {
    if (!pred || pred.home === '' || pred.away === '' || pred.home === undefined || pred.away === undefined) return false
    return parseInt(pred.home) === parseInt(pred.away)
  }

  async function handleSave() {
    if (locked) return
    setSaving(true)
    setSaved(false)
    const currentBracket = buildBracket(matches, predictions)
    const matchPreds = []
    Object.entries(predictions).forEach(([matchId, val]) => {
      if (val.home !== '' && val.away !== '' && val.home !== undefined && val.away !== undefined) {
        const match = matches.find(m => m.id === parseInt(matchId))
        const resolved = currentBracket[match?.match_number]
        let penWinnerId = null
        if (val.penalties_winner === 'home' && resolved?.home?.id) penWinnerId = resolved.home.id
        if (val.penalties_winner === 'away' && resolved?.away?.id) penWinnerId = resolved.away.id
        matchPreds.push({
          entry_id: entryId,
          match_id: parseInt(matchId),
          predicted_home_goals: val.home,
          predicted_away_goals: val.away,
          predicted_penalties_winner_id: penWinnerId,
          predicted_penalties_winner: val.penalties_winner || null,
          predicted_home_team_id: resolved?.home?.id || null,
          predicted_away_team_id: resolved?.away?.id || null,
          points_awarded: 0
        })
      }
    })
    const { error: saveError } = await supabase
      .from('match_predictions')
      .upsert(matchPreds, { onConflict: 'entry_id,match_id' })
    if (saveError) {
      setError('Something went wrong. Please try again.')
    } else {
      setSaved(true)
    }
    setSaving(false)
  }

  if (loading) return <div><nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav><div className="container"><p className="text-muted">Loading...</p></div></div>
  if (error) return <div><nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav><div className="container"><div className="card"><p style={{ color: 'var(--red)' }}>{error}</p></div></div></div>

  const bracket = buildBracket(matches, predictions)
  const activeMatches = matches.filter(m => m.phase === activePhase)

  return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <a href="/world-cup" className="btn btn-secondary" style={{ padding: '7px 14px', fontSize: '13px' }}>Dashboard</a>
          {!locked && <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '7px 14px', fontSize: '13px' }}>{saving ? 'Saving...' : 'Save'}</button>}
          {locked && <span style={{ fontSize: '13px', color: 'var(--red)' }}>Predictions locked</span>}
        </div>
      </nav>

      <div className="container">
        <div style={{ padding: '14px 18px', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: 'var(--r-lg)', marginBottom: '20px' }}>
          <p style={{ fontSize: '13px', fontWeight: '600', color: 'var(--red)', lineHeight: '1.6' }}>
            You must complete predictions for all rounds including the Final and the World Cup winner. Incomplete entries will not score points for rounds left blank.
          </p>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ marginBottom: '4px' }}>{entry.entry_name}</h1>
          <p className="text-muted">Knockout stage predictions - Phase 2</p>
        </div>

        {saved && (
          <div style={{ background: 'var(--gold-dim)', border: '1px solid var(--gold-border)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px' }}>
            <p style={{ color: 'var(--gold)', fontSize: '13px' }}>Predictions saved successfully.</p>
          </div>
        )}

        <div className="card mb-16" style={{ padding: '14px 18px' }}>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            Predict what you think the score will be at either the end of 90 mins or 120 mins. These will be counted the same. If you predict a draw, it will give you the opportunity to select who will win on penalties. Points are only awarded for predicting the correct team to go through, however they advance.
          </p>
        </div>

        <div className="tab-bar" style={{ marginBottom: '20px' }}>
          {PHASES.map(p => (
            <button key={p.key} className={"tab-btn " + (activePhase === p.key ? 'active' : '')} onClick={() => setActivePhase(p.key)}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '16px' }}>{PHASES.find(p => p.key === activePhase)?.label}</h3>
          {activeMatches.map(match => {
            const resolved = bracket[match.match_number]
            const pred = predictions[match.id] || {}
            const homeTeam = resolved?.home
            const awayTeam = resolved?.away
            const isReady = homeTeam && awayTeam
            const draw = isDraw(pred)

            return (
              <div key={match.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                  M{match.match_number} - {match.match_label}
                </p>
                {isReady ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                      <span style={{ flex: 1, fontSize: '13px' }}>{homeTeam.flag_emoji} {homeTeam.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input type="number" min="0" max="20" className="score-input" value={pred.home ?? ''} disabled={locked} style={{ opacity: locked ? 0.5 : 1 }} onChange={e => setScore(match.id, 'home', e.target.value)} />
                        <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>-</span>
                        <input type="number" min="0" max="20" className="score-input" value={pred.away ?? ''} disabled={locked} style={{ opacity: locked ? 0.5 : 1 }} onChange={e => setScore(match.id, 'away', e.target.value)} />
                      </div>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: '13px' }}>{awayTeam.name} {awayTeam.flag_emoji}</span>
                    </div>
                    {draw && (
                      <div style={{ marginTop: '10px', padding: '10px 12px', background: 'var(--bg-hover)', borderRadius: '8px' }}>
                        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Draw predicted - who wins on penalties?</p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => setPenaltyWinner(match.id, pred.penalties_winner === 'home' ? null : 'home')}
                            disabled={locked}
                            style={{
                              flex: 1, padding: '7px', borderRadius: '7px',
                              cursor: locked ? 'default' : 'pointer', fontSize: '12px', fontWeight: '500',
                              background: pred.penalties_winner === 'home' ? 'linear-gradient(135deg, #C9A84C, #E8C96A)' : 'var(--bg-card)',
                              color: pred.penalties_winner === 'home' ? '#080808' : 'var(--text-secondary)',
                              outline: pred.penalties_winner === 'home' ? 'none' : '1px solid var(--border-strong)'
                            }}
                          >
                            {homeTeam.flag_emoji} {homeTeam.name}
                          </button>
                          <button
                            onClick={() => setPenaltyWinner(match.id, pred.penalties_winner === 'away' ? null : 'away')}
                            disabled={locked}
                            style={{
                              flex: 1, padding: '7px', borderRadius: '7px',
                              cursor: locked ? 'default' : 'pointer', fontSize: '12px', fontWeight: '500',
                              background: pred.penalties_winner === 'away' ? 'linear-gradient(135deg, #C9A84C, #E8C96A)' : 'var(--bg-card)',
                              color: pred.penalties_winner === 'away' ? '#080808' : 'var(--text-secondary)',
                              outline: pred.penalties_winner === 'away' ? 'none' : '1px solid var(--border-strong)'
                            }}
                          >
                            {awayTeam.flag_emoji} {awayTeam.name}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ height: '36px', background: 'var(--bg-hover)', borderRadius: '8px', display: 'flex', alignItems: 'center', paddingLeft: '12px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {activePhase === 'knockout_r32' ? 'Teams not yet assigned' : 'Complete previous round first'}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {!locked && (
          <div style={{ textAlign: 'center', marginTop: '24px', paddingBottom: '40px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '13px 48px', fontSize: '15px' }}>
              {saving ? 'Saving...' : 'Save Predictions'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}